/* eslint-disable no-undef */
class CartDrawer extends HTMLElement {
  constructor () {
    super()
    this.openScrollPosition = 0

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close())
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this))
    this.setHeaderCartIconAccessibility()

    // Defer promo modal open until after the drawer fully closes
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('.promo-modal-trigger')
      if (!trigger) return

      event.preventDefault()
      event.stopPropagation()

      let fallbackTimer
      const openPromoAfterClose = () => {
        if (fallbackTimer) {
          clearTimeout(fallbackTimer)
          fallbackTimer = null
        }
        // Create a temporary trigger so the global promo-bar listener handles opening
        const tempTrigger = document.createElement('button')
        tempTrigger.type = 'button'
        tempTrigger.className = 'promo-modal-trigger sr-only'
        const sr = document.createElement('span')
        sr.className = 'sr-only'
        sr.textContent = 'Open promo modal'
        tempTrigger.appendChild(sr)
        document.body.appendChild(tempTrigger)
        tempTrigger.click()
        tempTrigger.remove()
      }

      this.addEventListener('transitionend', openPromoAfterClose, { once: true })
      // Fallback in case transitionend doesn’t fire
      fallbackTimer = setTimeout(openPromoAfterClose, 350)
      this.close()
    })

    // Setup references that may be re-bound on render
    this._bindScrollableRefs()
  }

  _bindScrollableRefs () {
    this.form = this.querySelector('#CartDrawer-Form')
    this.items = this.querySelector('#CartDrawer-CartItems')
    this.topFader = this.querySelector('.cart-drawer__fader--top')
    this.bottomFader = this.querySelector('.cart-drawer__fader--bottom')
    this.topSentinel = this.querySelector('.cart-drawer__sentinel--top')
    this.bottomSentinel = this.querySelector('.cart-drawer__sentinel--bottom')
  }

  _initFaders () {
    this._cleanupFaders()
    this._bindScrollableRefs()
    if (!this.form || !this.topFader || !this.bottomFader) return

    const isScrollable = this.form.scrollHeight > this.form.clientHeight
    if (!isScrollable) {
      this.topFader.classList.remove('show')
      this.bottomFader.classList.remove('show')
      return
    }

    if (this.topFader) this.topFader.classList.add('show')
    if (this.bottomFader) this.bottomFader.classList.add('show')

    if (this.topSentinel && this.bottomSentinel && 'IntersectionObserver' in window) {
      this._faderObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === this.topSentinel) {
            // If top sentinel is visible, we're at the very top; hide top fader
            if (entry.isIntersecting) {
              this.topFader.classList.remove('show')
            } else {
              this.topFader.classList.add('show')
            }
          } else if (entry.target === this.bottomSentinel) {
            // If bottom sentinel is visible, near bottom; hide bottom fader
            if (entry.isIntersecting) {
              this.bottomFader.classList.remove('show')
            } else {
              this.bottomFader.classList.add('show')
            }
          }
        }
      }, {
        root: this.form,
        threshold: 0.01,
      })

      this._faderObserver.observe(this.topSentinel)
      this._faderObserver.observe(this.bottomSentinel)
      return
    }

    // Fallback: scroll handler
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = this.form
      const atTop = scrollTop <= 0
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)
      const nearBottom = Math.ceil(distanceFromBottom) <= 5
      if (this.topFader) this.topFader.classList.toggle('show', !atTop)
      if (this.bottomFader) this.bottomFader.classList.toggle('show', !nearBottom)
    }
    this._scrollHandler = onScroll
    this.form.addEventListener('scroll', this._scrollHandler)
    // Initial state
    onScroll()
  }

  _cleanupFaders () {
    if (this._faderObserver) {
      try { this._faderObserver.disconnect() } catch (e) {}
      this._faderObserver = null
    }
    if (this.form && this._scrollHandler) {
      this.form.removeEventListener('scroll', this._scrollHandler)
      this._scrollHandler = null
    }
    if (this.topFader) this.topFader.classList.remove('show')
    if (this.bottomFader) this.bottomFader.classList.remove('show')
  }

  setHeaderCartIconAccessibility () {
    const cartLink = document.querySelector('#cart-icon-bubble')
    cartLink.setAttribute('role', 'button')
    cartLink.setAttribute('aria-haspopup', 'dialog')
    cartLink.addEventListener('click', (event) => {
      event.preventDefault()

      this.open(cartLink)
    })
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault()
        this.open(cartLink)
      }
    })
  }

  open (triggeredBy) {
    this.openScrollPosition = window.scrollY
    if (triggeredBy) this.setActiveElement(triggeredBy)
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary')
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote)

    window.dispatchEvent(new CustomEvent('close:modal'))

    setTimeout(() => {
      this.classList.add('animate', 'active')
      this.classList.remove('opacity-0')
    }, 1)

    allowPageScroll(false)
    document.body.classList.add('tb-overflow-hidden')
    document.documentElement.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${this.openScrollPosition}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'
    document.body.style.overflow = 'hidden'

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer')
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close')
        trapFocus(containerToTrapFocusOn, focusElement)
        this._initFaders()
      },
      { once: true },
    )

    window.dispatchEvent(new CustomEvent('show:page-obscure'))
    window.dispatchEvent(new CustomEvent('prevent:page-scroll'))

    // Initialize faders; fallback in case transitionend doesn't fire
    setTimeout(() => this._initFaders(), 0)
  }

  close () {
    this.classList.remove('active')
    this.classList.add('opacity-0')
    removeTrapFocus(this.activeElement)
    allowPageScroll(true)

    document.body.classList.remove('tb-overflow-hidden')
    const scrollY = this.openScrollPosition || 0

    requestAnimationFrame(() => {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.width = ''
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      window.scrollTo(0, scrollY)
      document.documentElement.scrollTop = scrollY
    })

    window.dispatchEvent(new CustomEvent('hide:page-obscure'))
    window.dispatchEvent(new CustomEvent('allow:page-scroll'))

    this._cleanupFaders()
  }

  setSummaryAccessibility (cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button')
    cartDrawerNote.setAttribute('aria-expanded', 'false')

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id)
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'))
    })

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape)
  }

  renderContents (parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty')
    this.productId = parsedState.id
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id)
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector)
    })

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this))
      this.open()
      // Re-init faders after content re-render
      this._initFaders()
    })
  }

  getSectionInnerHTML (html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML
  }

  getSectionsToRender () {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ]
  }

  getSectionDOM (html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
  }

  setActiveElement (element) {
    this.activeElement = element
  }
}

customElements.define('cart-drawer', CartDrawer)

window.addEventListener('cart:updated', () => {
  setTimeout(() => {
    if (window.tentboxInitCartDrawerFaders) window.tentboxInitCartDrawerFaders(document)
  }, 0)
})
